import { useCallback, useEffect, useRef, useState } from "react";
import type { CmmdWorkSession } from "@cmmd-forge/contracts";

import { createCmmdApiClient } from "~/environments/cmmdApi";
import { CmmdApiError } from "~/environments/cmmdApi/transport";
import { sanitizePublicUiError } from "~/productSafeLabels";

const api = createCmmdApiClient();

/** Lazily reads the durable execution sessions attached to the selected task. */
export function useTaskWorkSessions(projectId: string | null, taskId: number | null) {
  const [sessions, setSessions] = useState<readonly CmmdWorkSession[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scopeRef = useRef({ projectId, taskId });
  const requestRef = useRef(0);
  scopeRef.current = { projectId, taskId };

  useEffect(() => {
    requestRef.current += 1;
    setSessions([]);
    setLoaded(false);
    setLoading(false);
    setError(null);
  }, [projectId, taskId]);

  const load = useCallback(async () => {
    const scope = resolveScope(projectId, taskId);
    if (scope === null) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const nextSessions = await api.listProjectWorkSessions(scope.projectId, {
        taskId: scope.taskId,
      });
      if (!isCurrent(scopeRef.current, requestRef.current, scope, requestId)) return;
      setSessions(nextSessions);
      setLoaded(true);
    } catch (cause) {
      if (!isCurrent(scopeRef.current, requestRef.current, scope, requestId)) return;
      if (shouldClearSessions(cause)) setSessions([]);
      setError(sanitizePublicUiError(cause, "Task work sessions are unavailable."));
    } finally {
      if (isCurrent(scopeRef.current, requestRef.current, scope, requestId)) {
        setLoading(false);
      }
    }
  }, [projectId, taskId]);

  return { sessions, loaded, loading, error, load } as const;
}

interface TaskWorkSessionScope {
  readonly projectId: string;
  readonly taskId: number;
}

function resolveScope(
  projectId: string | null,
  taskId: number | null,
): TaskWorkSessionScope | null {
  return projectId === null || taskId === null ? null : { projectId, taskId };
}

function shouldClearSessions(cause: unknown): boolean {
  return cause instanceof CmmdApiError && (cause.status === 401 || cause.status === 403);
}

function isCurrent(
  scope: { readonly projectId: string | null; readonly taskId: number | null },
  activeRequestId: number,
  expected: TaskWorkSessionScope,
  requestId: number,
): boolean {
  return (
    scope.projectId === expected.projectId &&
    scope.taskId === expected.taskId &&
    activeRequestId === requestId
  );
}
